// StaffTheme — Hi-Contrast white-on-black + red mode for staff/admin surfaces.
// Wraps Dispatcher / Reservation Detail / Driver / Corporate pages.
//
// Behavior:
//   • On first visit, picks mode from `prefers-color-scheme`
//     (light OS → "hc", dark OS → "luxury").
//   • User toggle is sticky — stored in localStorage as "staff_theme_mode"
//     so once chosen, it overrides the auto detection.
//   • Apply / remove the `staff-hc-mode` class on <body> so portaled
//     modals (Sonner toasts, Radix dialogs) pick it up too.

import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { Sun, Moon, MonitorSmartphone } from "lucide-react";

const STORAGE_KEY = "staff_theme_mode";

const Ctx = createContext({ mode: "auto", isHc: false, setMode: () => {} });
export const useStaffTheme = () => useContext(Ctx);

const detectAuto = () => {
  try {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) return "hc";
  } catch (_) {}
  return "luxury";
};

export function StaffThemeProvider({ children }) {
  const [mode, _setMode] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "hc" || stored === "luxury" || stored === "auto") return stored;
    } catch (_) {}
    // Default to Swiss High-Contrast — matches the new customer-facing
    // design language. Holly can opt back into Luxury via the toggle.
    return "hc";
  });

  const isHc = useMemo(() => (mode === "hc" ? true : mode === "luxury" ? false : detectAuto() === "hc"), [mode]);

  useEffect(() => {
    document.body.classList.toggle("staff-hc-mode", isHc);
    document.documentElement.classList.toggle("staff-hc-mode", isHc);
    return () => {
      document.body.classList.remove("staff-hc-mode");
      document.documentElement.classList.remove("staff-hc-mode");
    };
  }, [isHc]);

  // Track OS preference change when in auto mode
  useEffect(() => {
    if (mode !== "auto" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => {
      document.body.classList.toggle("staff-hc-mode", mq.matches);
      document.documentElement.classList.toggle("staff-hc-mode", mq.matches);
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [mode]);

  const setMode = (next) => {
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) {}
    _setMode(next);
  };

  return <Ctx.Provider value={{ mode, isHc, setMode }}>{children}</Ctx.Provider>;
}

export function StaffThemeToggle({ compact = false }) {
  const { mode, isHc, setMode } = useStaffTheme();

  // Cycle: auto → hc → luxury → auto
  const next = mode === "auto" ? "hc" : mode === "hc" ? "luxury" : "auto";
  const label = mode === "auto" ? "Auto" : mode === "hc" ? "Hi-Contrast" : "Luxury";
  const Icon = mode === "auto" ? MonitorSmartphone : mode === "hc" ? Sun : Moon;
  const title = `Theme: ${label}${mode === "auto" ? ` · auto = ${isHc ? "Hi-Contrast" : "Luxury"}` : ""} · click to cycle`;

  return (
    <button
      type="button"
      data-testid="staff-theme-toggle"
      data-testid-mode={mode}
      onClick={() => setMode(next)}
      title={title}
      className={`text-[10px] uppercase tracking-[0.18em] border px-2.5 py-1.5 rounded inline-flex items-center gap-1.5 transition-colors ${
        isHc
          ? "border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
          : "border-white/20 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]"
      }`}
    >
      <Icon size={11}/>{!compact && label}
    </button>
  );
}
